/* eslint-disable camelcase */
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import swal from 'sweetalert';

import Loader from '../../../elements/Loader';
import FormFileGroup from '../../../elements/formElements/FormFileGroup';
import FormGroupText from '../../../elements/formElements/FormGroupText';
import axios from '../../../services/MyAxios';
import { API_CASINO_GAME_CATEGORY_BLOCK_UPLOAD_IMAGE } from '../Helpers/CrmApiUrls';
import { CASINO_GAME_CATEGORY_BLOCKS } from '../Helpers/CrmUrls';

interface Language {
    id: number;
    code: string;
    name: string;
}

interface Translation {
    language_id: number;
    name: string;
    redirect_url?: string;
}

interface GameCategory {
    id: number;
    casino_game_category_translations: { name: string }[];
}

interface GameCategoryBlock {
    name: string;
    casino_game_category_id: number;
    image_path: string;
    casino_game_category_block_translations: Translation[];
    translations: { [key: number]: { name: string; redirect_url?: string } };
}

interface GameCategoryBlockFormProps {
    gameCategoryBlockId: number;
    handleSubmit: (block: GameCategoryBlock) => void;
    errors: { [key: string]: string[] };
    translationErrors: { [key: string]: { name: string; redirect_url?: string; language_id: string } };
}

const GameCategoryBlockForm: React.FC<GameCategoryBlockFormProps> = ({
    gameCategoryBlockId,
    handleSubmit,
    errors,
    translationErrors,
}) => {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [translations, setTranslations] = useState<{ [key: number]: { name: string; redirect_url?: string } }>({});

    const [gameCategoryBlock, setGameCategoryBlock] = useState<GameCategoryBlock>({
        name: '',
        casino_game_category_id: 0,
        image_path: '',
        casino_game_category_block_translations: [],
        translations: [],
    });

    const [gameCategories, setGameCategories] = useState<GameCategory[]>([
        {
            id: 0,
            casino_game_category_translations: [],
        },
    ]);

    const uploadImage = (image: File) => {
        axios
            .post(
                API_CASINO_GAME_CATEGORY_BLOCK_UPLOAD_IMAGE,
                { image },
                {
                    headers: {
                        Accept: 'application/json',
                        'content-type': 'multipart/form-data',
                    },
                }
            )
            .then((response) => {
                const url = response.data.url;
                setGameCategoryBlock({
                    ...gameCategoryBlock,
                    image_path: url,
                });
            })
            .catch(() => {
                swal({
                    title: 'Error',
                    text: 'Server error',
                    icon: 'error',
                });
            });
    };

    const getGameCategories = () => {
        const url = '/crea/casino/api/data/game-categories';
        axios.get(url).then((response) => {
            const responseData = response.data;
            setGameCategories(responseData);
        });
    };

    const getGameCategoryBlock = (id: number) => {
        const url = `/crea/casino/api/data/game-category-block/${id}`;
        axios
            .get(url)
            .then((response) => {
                const data = response.data;
                data.casino_game_category_id = data.casino_game_category_id === null ? 0 : data.casino_game_category_id;

                setGameCategoryBlock(data);

                if (
                    data.casino_game_category_block_translations &&
                    data.casino_game_category_block_translations.length > 0
                ) {
                    const newTranslations = {};
                    data.casino_game_category_block_translations.forEach((translation) => {
                        newTranslations[translation.language_id] = {
                            name: translation.name,
                            redirect_url: translation.redirect_url,
                        };
                    });

                    setTranslations((prevTranslations) => ({
                        ...prevTranslations,
                        ...newTranslations,
                    }));
                }
            })
            .catch((error) => {
                if (error.response.status && error.response.status === 404) {
                    history.push({
                        pathname: CASINO_GAME_CATEGORY_BLOCKS,
                    });
                }
            });
    };

    const getLanguages = () => {
        axios.get('/crea/api/languages').then((response) => {
            setLanguages(response.data);
        });
    };

    useEffect(() => {
        getGameCategories();
        getLanguages();
        if (gameCategoryBlockId && gameCategoryBlockId !== 0) {
            getGameCategoryBlock(gameCategoryBlockId);
        }

        setIsLoading(false);
    }, [gameCategoryBlockId]);

    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { value, name } = e.target;

        setGameCategoryBlock({
            ...gameCategoryBlock,
            [name]: value,
        });
    };

    function hasErrorFor(field: string) {
        return !!errors[field];
    }

    function renderErrorFor(field: string) {
        if (hasErrorFor(field)) {
            return (
                <span className="invalid-feedback d-block">
                    <strong>{errors[field][0]}</strong>
                </span>
            );
        }

        return '';
    }

    const handleTranslationChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const languageId = parseInt(e.target.getAttribute('data-language-id'));

        setTranslations({
            ...translations,
            [languageId]: { ...translations[languageId], [name]: value },
        });
    };

    function getTranslationError(languageId, field) {
        if (translationErrors) {
            const translationErrorArray = Object.values(translationErrors);
            const foundError = translationErrorArray.find((error) => parseInt(error.language_id) === languageId);

            if (foundError) {
                return foundError[field];
            }
        }

        return undefined;
    }

    const getContent = () => {
        return (
            <div className="card">
                <div className="card-body">
                    <form className="form-flex" autoComplete="off" encType="multipart/form-data">
                        {renderErrorFor('translations')}
                        {languages.map((language) => {
                            const translatedCategoryBlock =
                                Object.keys(gameCategoryBlock.casino_game_category_block_translations).length > 0 &&
                                gameCategoryBlock.casino_game_category_block_translations.find(
                                    (translation) => translation.language_id === language.id
                                );
                            return (
                                <div key={language.code}>
                                    <FormGroupText
                                        name="name"
                                        key={`${language.id}_name`}
                                        label={`${language.name} name`}
                                        value={
                                            translations[language.id]?.name ??
                                            translatedCategoryBlock?.name ??
                                            undefined
                                        }
                                        handleChange={handleTranslationChange}
                                        error={getTranslationError(language.id, 'name')}
                                        data-language-id={language.id}
                                        inputClasses="w-75"
                                    />
                                    {gameCategoryBlock.casino_game_category_id === 0 && (
                                        <FormGroupText
                                            name="redirect_url"
                                            key={`${language.id}_redirect_url`}
                                            label={`${language.name} redirect url`}
                                            value={
                                                translations[language.id]?.redirect_url ??
                                                translatedCategoryBlock?.redirect_url ??
                                                undefined
                                            }
                                            handleChange={handleTranslationChange}
                                            error={getTranslationError(language.id, 'redirect_url')}
                                            data-language-id={language.id}
                                            inputClasses="w-75"
                                        />
                                    )}
                                </div>
                            );
                        })}
                        <FormFileGroup
                            label="Image 100px x 100px in .png format"
                            field="image_path"
                            value={gameCategoryBlock.image_path}
                            error={errors.image_path ?? undefined}
                            handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                                uploadImage(event.target.files[0]);
                            }}
                            classes="category-block-image dark-transparent-background"
                            acceptExtensions=".png"
                        />
                        <div className="form-group">
                            <label htmlFor="casino_game_category_id">
                                This block is for category <br />
                            </label>
                            <select
                                className="p-2"
                                name="casino_game_category_id"
                                value={gameCategoryBlock.casino_game_category_id}
                                onChange={handleChange}
                            >
                                <option key="casino_game_category_id_default" value="0">
                                    Custom redirect
                                </option>
                                {gameCategories.length > 0 &&
                                    gameCategories.map((gameCategory) => {
                                        return (
                                            <option
                                                key={gameCategory.id}
                                                value={gameCategory.id}
                                                defaultValue={gameCategoryBlock.casino_game_category_id}
                                            >
                                                {gameCategory.casino_game_category_translations.length > 0
                                                    ? gameCategory.casino_game_category_translations[0].name
                                                    : undefined}
                                            </option>
                                        );
                                    })}
                            </select>

                            {renderErrorFor('casino_game_category_id')}
                        </div>

                        <div className="form-group">
                            <button
                                onClick={() =>
                                    history.push({
                                        pathname: CASINO_GAME_CATEGORY_BLOCKS,
                                    })
                                }
                                type="button"
                                className="btn btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    handleSubmit({
                                        ...gameCategoryBlock,
                                        translations,
                                    });
                                }}
                                className="btn btn-primary"
                            >
                                Save
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return <Loader loading={isLoading} content={getContent} />;
};

export default GameCategoryBlockForm;
